import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Modal, Row } from "react-bootstrap";
import ErrorDisplay from "../common/ErrorDisplay";
import { Problem } from "../api/backendApi";
import { glossaryAttributeTextStringApi } from "../api/glossaryAttributeTextStringApi";
import { glossaryStandardTextStringApi } from "../api/glossaryStandardTextStringApi";

interface TextWarningModalProps {
  unmatchedStrings: string[];
  knownUnmatchedStrings: string[];
  onSave: () => void;
  onClose: () => void;
}

function TextWarningModal({
  unmatchedStrings,
  knownUnmatchedStrings,
  onSave,
  onClose,
}: TextWarningModalProps) {
  const [error, setError] = useState<Problem>();
  const [error2, setError2] = useState<Problem>();
  const [attributeTextsToAdd, setAttributeTextsToAdd] = useState<string[]>([]);
  const [standardTextsToAdd, setStandardTextsToAdd] = useState<string[]>([]);

  useEffect(() => {
    setAttributeTextsToAdd(unmatchedStrings);
  }, [unmatchedStrings, knownUnmatchedStrings]);

  const handleSave = async () => {
    if (attributeTextsToAdd.length === 0 && standardTextsToAdd.length === 0) {
      onSave();
      return;
    }

    if (attributeTextsToAdd.length !== 0) {
      const result =
        await glossaryAttributeTextStringApi.addNewAttributeTexts(
          attributeTextsToAdd,
        );

      if (!result.ok) {
        setError(result.problem);
        return;
      }
    }

    if (standardTextsToAdd.length !== 0) {
      const result =
        await glossaryStandardTextStringApi.addNewStandardTexts(
          standardTextsToAdd,
        );

      if (!result.ok) {
        setError2(result.problem);
        return;
      }
    }

    onSave();
  };

  const handleChange = (value: string, type: string) => {
    if (type === "Attribute Text") {
      setStandardTextsToAdd(
        standardTextsToAdd.filter((text) => text !== value),
      );
      if (attributeTextsToAdd.includes(value)) {
        // Do nothing
      } else {
        setAttributeTextsToAdd([...attributeTextsToAdd, value]);
      }
    } else if (type === "Title Text") {
      setAttributeTextsToAdd(
        attributeTextsToAdd.filter((text) => text !== value),
      );
      if (standardTextsToAdd.includes(value)) {
        // Do nothing
      } else {
        setStandardTextsToAdd([...standardTextsToAdd, value]);
      }
    }
  };

  return (
    <Modal show={true} onHide={() => onClose()} size="lg">
      <Modal.Header>
        <Modal.Title>Text validation warnings</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container>
          <ErrorDisplay error={error} />
          <ErrorDisplay error={error2} />
          <Row>
            <Col>
              <table className="table">
                <thead>
                  <tr>
                    <th>Unknown strings</th>
                    <th className="text-nowrap">Add to new</th>
                  </tr>
                </thead>
                <tbody>
                  {unmatchedStrings.length === 0 && (
                    <tr>
                      <td colSpan={2}>none...</td>
                    </tr>
                  )}
                  {unmatchedStrings.map((s, index) => (
                    <tr key={index}>
                      <td>
                        <span
                          className="d-inline-block text-truncate"
                          style={{ maxWidth: "250px" }}
                        >
                          {s}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <Form.Select
                          onChange={(e) => {
                            handleChange(s, e.target.value);
                          }}
                        >
                          <option>Attribute Text</option>
                          <option>Title Text</option>
                        </Form.Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Col>
            <Col>
              <table className="table">
                <thead>
                  <tr>
                    <th>Already known strings</th>
                  </tr>
                </thead>
                <tbody>
                  {knownUnmatchedStrings.length === 0 && (
                    <tr>
                      <td>none...</td>
                    </tr>
                  )}
                  {knownUnmatchedStrings.map((s, index) => (
                    <tr key={index}>
                      <td>
                        <span
                          className="d-inline-block text-truncate"
                          style={{ maxWidth: "250px" }}
                        >
                          {s}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm" onClick={() => onClose()}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => handleSave()}>
          {unmatchedStrings.length > 0
            ? "Continue and add selected text"
            : "Continue"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default TextWarningModal;
